import { useEffect, useRef, type RefObject } from 'react';

import { passedThreshold, type Point } from '../lib/pointer-drag';
import { rubberBand, shouldCommitSwipe, SWIPE_THRESHOLD_PX } from '../lib/swipe';

export interface SwipeOptions {
  axis: 'x' | 'y';
  /** Sign that counts as a swipe; 'positive' = right/down. Movement the other way rubber-bands. */
  direction?: 'positive' | 'negative' | 'both';
  /** Distance that commits regardless of speed. Default SWIPE_THRESHOLD_PX. */
  threshold?: number;
  disabled?: boolean;
  /** Return false to refuse the gesture (e.g. a sheet body not at scrollTop 0). */
  canStart?: (event: PointerEvent) => boolean;
  /** Per move once armed. Default writes translate3d to the element. */
  onMove?: (delta: number) => void;
  /** Fires on release, after the default transform reset, with the final delta. */
  onEnd?: (committed: boolean, delta: number) => void;
  onSwipe: () => void;
}

interface Gesture {
  pointerId: number;
  origin: Point;
  startedAt: number;
  armed: boolean;
  delta: number;
}

/**
 * A single-axis swipe on one element, via Pointer Events.
 *
 * Deliberately not `usePointerDrag`: that hook is a list-reorder drag with
 * hold-to-arm on touch and drop-zone hit-testing. This one only needs to know
 * how far, how fast, and which way. Movement is written straight to the node
 * (never React state), the same rule the drag hook follows, so a 120Hz finger
 * never waits on a render.
 *
 * Consumers set `touch-action` on the surface themselves — `pan-y` for an
 * x-axis swipe so the page still scrolls, `none` for a dedicated grip.
 */
export function useSwipe<T extends HTMLElement>(
  ref: RefObject<T | null>,
  options: SwipeOptions,
): void {
  const latest = useRef(options);
  latest.current = options;

  useEffect(() => {
    const el = ref.current;
    if (!el || options.disabled === true) return undefined;

    let gesture: Gesture | null = null;

    const axisOf = (event: PointerEvent): number =>
      latest.current.axis === 'x' ? event.clientX : event.clientY;

    const applyMove = (delta: number): void => {
      const move = latest.current.onMove;
      if (move) {
        move(delta);
        return;
      }
      el.style.transform =
        latest.current.axis === 'x'
          ? `translate3d(${delta}px, 0, 0)`
          : `translate3d(0, ${delta}px, 0)`;
    };

    const resetMove = (): void => {
      if (latest.current.onMove) return;
      el.style.transform = '';
    };

    const allowedSign = (delta: number): boolean => {
      const direction = latest.current.direction ?? 'both';
      if (direction === 'both') return true;
      return direction === 'positive' ? delta > 0 : delta < 0;
    };

    const finish = (committed: boolean): void => {
      if (gesture === null) return;
      const wasArmed = gesture.armed;
      const delta = gesture.delta;
      gesture = null;
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      document.removeEventListener('keydown', onKeyDown);
      if (!wasArmed) return;
      resetMove();
      latest.current.onEnd?.(committed, delta);
      if (committed) latest.current.onSwipe();
    };

    const onPointerMove = (event: PointerEvent): void => {
      if (gesture === null || event.pointerId !== gesture.pointerId) return;
      const point = { x: event.clientX, y: event.clientY };
      if (!gesture.armed) {
        if (!passedThreshold(gesture.origin, point)) return;
        // Only a mostly-along-the-axis move arms; a diagonal that is really a
        // scroll is left to the browser.
        const dx = Math.abs(point.x - gesture.origin.x);
        const dy = Math.abs(point.y - gesture.origin.y);
        const alongAxis = latest.current.axis === 'x' ? dx >= dy : dy >= dx;
        if (!alongAxis) {
          finish(false);
          return;
        }
        gesture.armed = true;
      }
      if (event.cancelable) event.preventDefault();
      const raw =
        axisOf(event) - (latest.current.axis === 'x' ? gesture.origin.x : gesture.origin.y);
      const threshold = latest.current.threshold ?? SWIPE_THRESHOLD_PX;
      gesture.delta = allowedSign(raw) ? raw : rubberBand(raw, threshold / 4);
      applyMove(gesture.delta);
    };

    const onPointerUp = (event: PointerEvent): void => {
      if (gesture === null || event.pointerId !== gesture.pointerId) return;
      const threshold = latest.current.threshold ?? SWIPE_THRESHOLD_PX;
      const elapsed = performance.now() - gesture.startedAt;
      const committed =
        gesture.armed &&
        allowedSign(gesture.delta) &&
        shouldCommitSwipe(gesture.delta, elapsed, threshold);
      finish(committed);
    };

    const onPointerCancel = (event: PointerEvent): void => {
      if (gesture === null || event.pointerId !== gesture.pointerId) return;
      finish(false);
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') finish(false);
    };

    const onPointerDown = (event: PointerEvent): void => {
      if (gesture !== null || event.button !== 0) return;
      if (latest.current.canStart?.(event) === false) return;
      gesture = {
        pointerId: event.pointerId,
        origin: { x: event.clientX, y: event.clientY },
        startedAt: performance.now(),
        armed: false,
        delta: 0,
      };
      try {
        el.setPointerCapture(event.pointerId);
      } catch {
        /* jsdom, or a pointer that is already gone */
      }
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerCancel);
      document.addEventListener('keydown', onKeyDown);
    };

    el.addEventListener('pointerdown', onPointerDown);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      finish(false);
    };
  }, [ref, options.disabled]);
}
